import { ProductVariationBase } from 'models/productsReferential';
import type { OrdersBasketTabContent } from 'state/orders-basket/models';
import type { AppState } from 'state/state.model';
import { getWorkspaceTabIds } from 'state/workspaces/workspaces/workspaces.selector';

import { isOrdersBasketWorkspaceTabsFetched } from '../meta/meta.selectors';
import type { OrdersBasketWorkspaceTabContent, OrdersBasketWorkspaceTabContentState } from './tabContent.model';

function selectOrdersBasketWorkspaceTabContentState(appState: AppState): OrdersBasketWorkspaceTabContentState {
  return appState.workspaces.ordersBasket.tabContent;
}

export function getOrdersBasketWorkspaceTabContentById(
  appState: AppState,
  tabId: string,
): OrdersBasketWorkspaceTabContent | undefined {
  const state = selectOrdersBasketWorkspaceTabContentState(appState);
  return state[tabId];
}

export function getOrdersBasketWorkspaceTabContentForPersistenceById(
  appState: AppState,
  tabId: string,
): OrdersBasketTabContent | undefined {
  const state = selectOrdersBasketWorkspaceTabContentState(appState);
  return state[tabId]
    ? {
        tabId: state[tabId].tabId,
        workspace: 'ordersBasket',
        negotiationIds: state[tabId].formIds,
        wrapperType: state[tabId].wrapperType,
        productSubtype: state[tabId].productSubtype,
        productType: state[tabId].productType,
      }
    : undefined;
}

export function getOrdersBasketWorkspaceTabContentByProductDescription(
  appState: AppState,
  productDescription: ProductVariationBase,
): OrdersBasketWorkspaceTabContent | undefined {
  const state = selectOrdersBasketWorkspaceTabContentState(appState);
  return Object.values(state).find(
    tabContent =>
      tabContent.productSubtype?.toLowerCase() === productDescription.productSubType?.toLowerCase() &&
      tabContent.productType?.toLowerCase() === productDescription.productType?.toLowerCase(),
  );
}

export function getOrdersBasketWorkspaceTabIdByProductSubType(
  appState: AppState,
  productDescription: ProductVariationBase,
): string | undefined {
  const tab = getOrdersBasketWorkspaceTabContentByProductDescription(appState, productDescription);
  return tab?.tabId;
}

export function getOrdersBasketFormIdsByProductVariation(
  appState: AppState,
  productDescription: ProductVariationBase,
): readonly string[] {
  const tab = getOrdersBasketWorkspaceTabContentByProductDescription(appState, productDescription);
  return tab?.formIds ?? emptyArray;
}

export function getNumberOfOrdersBasketFormsByProductVariation(
  appState: AppState,
  productVariation: ProductVariationBase,
): number {
  const formIds = getOrdersBasketFormIdsByProductVariation(appState, productVariation);
  return formIds.length;
}

export function getOrdersBasketTabIdByFormId(appState: AppState, formId: string): string | undefined {
  const state = selectOrdersBasketWorkspaceTabContentState(appState);
  const match = Object.values(state).find(tabContent => tabContent.formIds.includes(formId));
  return match?.tabId;
}

const emptyArray: readonly [] = [];
export function getOrdersBasketFormIdsByTabId(appState: AppState, tabId: string): readonly string[] {
  const tabContent = getOrdersBasketWorkspaceTabContentById(appState, tabId);
  return tabContent ? tabContent.formIds : emptyArray;
}

export function isAllOrdersBasketTabContentsFetched(appState: AppState): boolean {
  const ordersBasketWorkspaceTabFetched = isOrdersBasketWorkspaceTabsFetched(appState);
  if (!ordersBasketWorkspaceTabFetched) {
    return false;
  }

  const tabIds = getWorkspaceTabIds(appState, 'ordersBasket');
  return tabIds.every(tabId => {
    const tabContent = getOrdersBasketWorkspaceTabContentById(appState, tabId);
    return tabContent ? !tabContent.isFetchingTabContent : false;
  });
}
